import React, { memo } from 'react';
import PropTypes from 'prop-types';

import PaidSection from 'components/paid-section';
import Typography from 'components/ui-kit/typography';
import Money from 'components/ui-kit/money';
import Stack from 'components/ui-kit/stack';
import Group from 'components/ui-kit//group';

import { calculateEntitySummary } from 'helpers/calculations.helper';
import {
  getDepositPaidLabel, getPaymentChargeLabel, getSurchargeLabel, getTipLabel,
} from 'helpers/payment-display.helper';
import { formatDate } from 'helpers/datetime.helper';

import SubtotalSection from './components/subtotal-section';
import LineItemsSection from './components/line-items-section';
import DepositDueSection from './components/deposit-due-section';
import CreditSection from './components/credit-section';

import { getColumns } from './utils';

import * as S from './styles';

const PaymentSummary = ({
  title,
  lineItems,
  taxRate,
  discount,
  isPaid,
  setLineItems,
  withOptional,
  disabled,
  preCalculatedSummary,
  displayQuantityAndUnitPrice,
  appliedCcSurcharge,
  appliedTip,
  depositPaidAmount,
  amountDue,
  depositPaymentMethod,
  depositPaidDate,
  payments,
  requiredDeposit,
  hideTotals,
  creditToBeApplied,
}) => {
  const {
    requiredLineItems,
    optionalLineItems,
    requiredSubtotal,
    optionalSubtotal,
    subtotalAmount,
    discountAmount,
    taxAmount,
    totalAmount,
  } = calculateEntitySummary({
    lineItems,
    taxRate,
    discount,
    preCalculatedSummary,
  });

  const columns = getColumns({ displayQuantityAndUnitPrice });
  const surchargeAmount = appliedCcSurcharge?.appliedAmount || 0;
  const tipAmount = appliedTip?.appliedAmount || 0;
  const hasAdjustments = surchargeAmount > 0 || tipAmount > 0;
  const totalPaid = totalAmount + surchargeAmount + tipAmount;
  const paidCharges = (payments || []).filter((p) => p.status === 2 && !p.refundDate);
  // Credit never applies to a required deposit (same gate the backend applies before charging it),
  // so a deposit still owed takes over this slot instead - never both at once.
  const showCredit = !isPaid && !requiredDeposit && creditToBeApplied > 0;

  return (
    <Stack gap={24}>
      <Group justifyContent="space-between">
        <Typography as="div" fontFamily="semibold">{title}</Typography>

        {isPaid && <PaidSection justifyContent="end" />}
      </Group>

      {!!requiredLineItems.length && (
        <Stack>
          <LineItemsSection
            lineItems={lineItems}
            sectionLineItems={requiredLineItems}
            subtitle="Service / Material"
            columns={columns}
          />

          {!hideTotals && !!optionalLineItems.length && <SubtotalSection title="Total:" amount={requiredSubtotal} />}
        </Stack>
      )}

      {withOptional && !!optionalLineItems.length && (
        <Stack>
          <LineItemsSection
            lineItems={lineItems}
            sectionLineItems={optionalLineItems}
            subtitle="Options"
            columns={columns}
            setLineItems={setLineItems}
            isOptional
            disabled={disabled}
          />

          {!hideTotals && !!requiredLineItems.length && <SubtotalSection title="Total:" amount={optionalSubtotal} />}
        </Stack>
      )}

      {!hideTotals && (
        <>
          <S.SummaryTail>
            <Stack gap={8}>
              <SubtotalSection title="Subtotal:" amount={subtotalAmount} />
              {!!discount && <SubtotalSection title={`Discount (${discount.name}):`} amount={-discountAmount} />}

              {!!taxRate && <SubtotalSection title={`Tax (${taxRate.name}):`} amount={taxAmount} />}

              {tipAmount > 0 && (
                <SubtotalSection
                  title={getTipLabel(appliedTip?.tipPercentage)}
                  amount={tipAmount}
                />
              )}

              {surchargeAmount > 0 && (
                <SubtotalSection
                  title={getSurchargeLabel(appliedCcSurcharge)}
                  amount={surchargeAmount}
                />
              )}

              {paidCharges.length > 0 ? (
                paidCharges.map((charge) => (
                  <SubtotalSection
                    key={charge.id}
                    title={getPaymentChargeLabel(
                      charge.method,
                      charge.date ? formatDate(charge.date) : null,
                      charge.isDeposit,
                    )}
                    amount={-charge.amount}
                  />
                ))
              ) : (
                depositPaidAmount > 0 && (
                  <SubtotalSection
                    title={getDepositPaidLabel(
                      depositPaymentMethod,
                      depositPaidDate ? formatDate(depositPaidDate) : null,
                    )}
                    amount={-depositPaidAmount}
                  />
                )
              )}

              {amountDue > 0 && (paidCharges.length > 0 || depositPaidAmount > 0) && (
                <SubtotalSection title="Balance remaining:" amount={amountDue} />
              )}
            </Stack>
          </S.SummaryTail>

          <S.Divider dark />

          <S.SummaryTail>
            <S.TotalRow>
              <Typography fontFamily="semibold" fontWeight={700}>
                {hasAdjustments ? 'TOTAL PAID:' : 'TOTAL:'}
              </Typography>

              <Typography fontFamily="semibold" fontWeight={700}>
                <Money amount={hasAdjustments ? totalPaid : totalAmount} />
              </Typography>
            </S.TotalRow>

            {!!requiredDeposit && (
              <DepositDueSection
                {...requiredDeposit}
                total={totalAmount}
                pendingCredit={!isPaid ? creditToBeApplied : 0}
              />
            )}

            {showCredit && (
              <CreditSection
                creditApplied={creditToBeApplied}
                amountAfterCredit={Math.max((amountDue ?? totalAmount) - creditToBeApplied, 0)}
              />
            )}
          </S.SummaryTail>
        </>
      )}
    </Stack>
  );
};

PaymentSummary.propTypes = {
  title: PropTypes.string,
  lineItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  taxRate: PropTypes.shape({
    name: PropTypes.string,
  }),
  discount: PropTypes.shape({
    name: PropTypes.string,
  }),
  isPaid: PropTypes.bool,
  setLineItems: PropTypes.func,
  withOptional: PropTypes.bool,
  disabled: PropTypes.bool,
  preCalculatedSummary: PropTypes.shape({
    subtotalAmount: PropTypes.number,
    discountAmount: PropTypes.number,
    taxAmount: PropTypes.number,
    totalAmount: PropTypes.number,
  }),
  displayQuantityAndUnitPrice: PropTypes.bool,
  appliedCcSurcharge: PropTypes.shape({
    surchargeName: PropTypes.string,
    appliedAmount: PropTypes.number,
  }),
  appliedTip: PropTypes.shape({
    appliedAmount: PropTypes.number,
    tipPercentage: PropTypes.number,
  }),
  depositPaidAmount: PropTypes.number,
  amountDue: PropTypes.number,
  depositPaymentMethod: PropTypes.number,
  depositPaidDate: PropTypes.string,
  payments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.string,
    amount: PropTypes.number,
    method: PropTypes.number,
    status: PropTypes.number,
    isDeposit: PropTypes.bool,
    refundDate: PropTypes.string,
  })),
  requiredDeposit: PropTypes.shape({
    amount: PropTypes.number,
    amountType: PropTypes.number,
    rawAmount: PropTypes.number,
  }),
  hideTotals: PropTypes.bool,
  creditToBeApplied: PropTypes.number,
};

PaymentSummary.defaultProps = {
  title: 'Payment Summary',
  taxRate: null,
  discount: null,
  isPaid: false,
  setLineItems: () => {},
  withOptional: false,
  disabled: false,
  preCalculatedSummary: {},
  displayQuantityAndUnitPrice: true,
  appliedCcSurcharge: null,
  appliedTip: null,
  depositPaidAmount: 0,
  amountDue: null,
  depositPaymentMethod: null,
  depositPaidDate: null,
  payments: [],
  requiredDeposit: null,
  hideTotals: false,
  creditToBeApplied: 0,
};

export default memo(PaymentSummary);
